<template>
  <page-header title="Sessions">
    <button v-if="user.tier === 'admin'" type="button" class="btn-primary ml-auto h-10" @click="terminateAll">
      <FireIcon class="size-5 -ml-1 mr-2" />
      Terminate All
    </button>
  </page-header>

  <grid-items-empty v-if="isEmpty && search === null" />

  <template v-else>
    <div class="flex justify-between items-stretch mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <div class="flex items-center gap-x-1.5 text-slate-400">
        <span class="text-xs">All</span>
        <form-switch v-model="isUsersOnly" class="flex" />
        <span class="text-xs">Active Users</span>
      </div>

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getSessions"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @terminate="terminate"
      @download-logs="downloadLogs"
      @download-video="downloadVideo"
    />
  </template>
</template>

<script setup>
import { ref, markRaw, computed } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import SessionListActions from '../components/SessionListActions.vue';
import Grid from '../components/grid/Grid.vue';
import SessionUserIcon from '../components/SessionUserIcon.vue';
import SessionProgramIcon from '../components/SessionProgramIcon.vue';
import FormSwitch from '../components/FormSwitch.vue';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import { FireIcon } from '@heroicons/vue/24/outline';
import { getUser } from '../composables/useAuth';
import { formatDate } from '../utils/formatters';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();

// Loading states for download buttons
const downloadingLogs = ref(new Set());
const downloadingVideo = ref(new Set());

const user = getUser();

function rowClass(_row, index) {
  if (_row.tier === 'admin') {
    return 'bg-primary-100/50';
  }
  if (_row.tier === 'seo') {
    return 'bg-fuchsia-100/50';
  }
  return (index % 2 ? 'bg-zinc-300/10' : '') + (_row.deletedAt ? ' [&>td:not(:last-child)]:!opacity-30' : ''); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const filter = useRouteQuery('filter', null);
if (user.value.tier === 'seo') {
  filter.value = 'users';
}

const isUsersOnly = computed({
  get: () => {
    return filter.value === 'users';
  },
  set: value => {
    filter.value = value ? 'users' : null;
  }
});

const search = useRouteQuery('search', null);
watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = {
    limit,
    offset,
    order: sort,
    direction: direction?.toLowerCase(),
    search: search.value ?? '',
    filter: filter.value ?? ''
  };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/session/?${searchParams}`;
}

async function getSessions(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  return {
    total: data.value.total,
    rows: data.value.sessions
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'cursor-pointer text-xs font-mono',
    sortable: true,
    click: row => user.value.tier === 'admin' ? router.push(`/session/${row.id}`) : null
  },
  {
    title: 'State',
    prop: 'state',
    sortable: true
  },
  {
    title: 'Duration',
    class: 'text-right',
    value: row => {
      if (!row.finishedAt) {
        return '-';
      }

      const duration = new Date(row.finishedAt) - new Date(row.startedAt || row.createdAt);
      const hours = Math.floor(duration / 1000 / 60 / 60);
      let minutes = Math.floor((duration / 1000 / 60) % 60);
      if (minutes < 10) {
        minutes = `0${minutes}`;
      }
      let seconds = Math.floor((duration / 1000) % 60);
      if (seconds < 10) {
        seconds = `0${seconds}`;
      }

      return `${hours}:${minutes}:${seconds}`;
    }
  },
  {
    title: 'User',
    prop: 'userId',
    class: 'cursor-pointer !py-0',
    component: markRaw(SessionUserIcon),
    click: row => user.value.tier === 'admin' ? router.push(`/user/${row.userId}`) : null,
    sortable: true
  },
  {
    title: 'Program',
    prop: 'programId',
    class: 'cursor-pointer !py-0',
    component: markRaw(SessionProgramIcon),
    click: row => router.push(`/program/${row.programId}`),
    sortable: true
  },
  {
    title: 'Date',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt),
    sortable: true
  },
  {
    title: 'Last Activity',
    prop: 'lastActivityAt',
    value: row => row.lastActivityAt ? formatDate(row.lastActivityAt) : '-',
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(SessionListActions),
    props: row => ({
      downloadingLogs: downloadingLogs.value.has(row.id),
      downloadingVideo: downloadingVideo.value.has(row.id)
    })
  }
];

async function terminateAll() {
  const confirm = await Modal.confirm({
    title: 'Unrevertable changes',
    body: 'You going to do unrevertable changes. <br/> Are you sure you want to terminate all sessions?',
    okButton: 'Kill`em all',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/session/terminate-all`).post({ filter: filter.value }).json();

  if (!data.value?.success) {
    toast.error('Something went wrong');
    return;
  }

  reloadGrid();
  toast.success(`All ${data.value.terminated} sessions terminated successfully`);
}

async function terminate({ id }) {
  const confirm = await Modal.confirm({
    title: 'Terminate Session',
    body: `Terminate the session #${id}?`,
    okButton: 'Terminate',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/session/terminate/${id}`).get().json();

  if (!data.value?.success) {
    toast.error('Error terminating session');
    return;
  }

  reloadGrid();
  toast.success('Session terminated successfully');
}

async function downloadLogs({ id }) {
  // Prevent multiple clicks
  if (downloadingLogs.value.has(id)) {
    return;
  }

  downloadingLogs.value.add(id);
  toast.info('Starting logs download...');

  try {
    const response = await fetch(`${apiDomain}/api/recording/${id}/logs`);
    const data = await response.json();

    console.log('Data:', data);

    if (!response.ok) {
      // Check for specific "not found" error
      if (data?.error === 'No files found for this session' || data?.error === 'No log files found for this session') {
        toast.warning('No log files found for this session');
      } else {
        toast.error('Error fetching logs');
      }
      return;
    }

    if (!data?.logs?.consoleLog?.downloadUrl) {
      toast.warning('No log files found for this session');
      return;
    }

    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = data.logs.consoleLog.downloadUrl;
    link.download = data.logs.consoleLog.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Logs download started');
  } finally {
    // Remove loading state after a short delay to prevent rapid clicking
    setTimeout(() => {
      downloadingLogs.value.delete(id);
    }, 1000);
  }
}

async function downloadVideo({ id }) {
  // Prevent multiple clicks
  if (downloadingVideo.value.has(id)) {
    return;
  }

  downloadingVideo.value.add(id);
  toast.info('Starting video download...');

  try {
    const response = await fetch(`${apiDomain}/api/recording/${id}/full-recording`);
    const data = await response.json();

    console.log('Data:', data);

    if (!response.ok) {
      // Check for specific "not found" error
      if (data?.error === 'No recording chunks found for this session' || data?.error === 'No valid recording chunks found') {
        toast.warning('No recording found for this session');
      } else {
        toast.error('Error fetching video');
      }
      return;
    }

    if (!data?.downloadUrl) {
      toast.warning('No recording found for this session');
      return;
    }

    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = data.filename || `recording-${id}.webm`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Video download started');
  } finally {
    // Remove loading state after a short delay to prevent rapid clicking
    setTimeout(() => {
      downloadingVideo.value.delete(id);
    }, 1000);
  }
}
</script>
